import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, Bell, Shield, LogOut, Trash2, ChevronRight, 
  DollarSign, Calendar, Wallet, Edit2, Share2, Copy, Check, Globe,
  Settings, Smartphone, CheckCircle, XCircle, AlertCircle, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import MobileNav from '../components/MobileNav';
import { CURRENCIES, getCurrencySymbol, formatCurrency } from '../utils/currency';
import { LegalFooter } from '../components/LegalFooter';

// Use window.location.origin for API calls to work on any domain
const API = `${window.location.origin}/api`;
// Use production URL for sharing - always share the live domain
const APP_URL = 'https://paydaycontrol.app';

export default function SettingsPage() {
  const { user, logout, deleteAccount, updateCurrency } = useAuth();
  const navigate = useNavigate();
  
  const [balance, setBalance] = useState(null);
  const [paySchedule, setPaySchedule] = useState(null);
  const [notificationPrefs, setNotificationPrefs] = useState({
    daily_summary: false,
    bill_reminders: false,
    low_balance_alert: false,
    payday_reminder: false
  });
  const [pushSupported, setPushSupported] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(user?.currency || 'GBP');
  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Notification setup modal states
  const [showNotificationGuide, setShowNotificationGuide] = useState(false);
  const [notificationStep, setNotificationStep] = useState('checking'); // 'checking', 'blocked', 'success'
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [showPayScheduleModal, setShowPayScheduleModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [newBalance, setNewBalance] = useState('');
  const [newPaySchedule, setNewPaySchedule] = useState({
    pay_frequency: 'monthly',
    next_payday: ''
  });

  useEffect(() => {
    fetchData();
    checkPushSupport();
    
    // Detect iOS device
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(iOS);
    
    // Detect standalone/PWA mode
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);
    
    console.log('[Push] Device detection:', { iOS, standalone });
    
    // Re-check push state when app becomes visible (user returns from settings)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[Push] App became visible - re-checking permission state');
        checkPushSupport();
        
        // If guide is open and we're checking, re-verify
        if (showNotificationGuide && notificationStep === 'blocked') {
          const currentPermission = Notification.permission;
          if (currentPermission === 'granted') {
            // Permission was granted in settings, try to complete setup
            completeNotificationSetup();
          }
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [showNotificationGuide, notificationStep]);

  useEffect(() => {
    if (user?.currency) {
      setSelectedCurrency(user.currency);
    }
  }, [user?.currency]);

  const fetchData = async () => {
    try {
      const [balanceRes, scheduleRes, prefsRes] = await Promise.all([
        fetch(`${API}/balance`, { credentials: 'include' }),
        fetch(`${API}/forecast/pay-schedule`, { credentials: 'include' }),
        fetch(`${API}/notifications/preferences`, { credentials: 'include' })
      ]);

      if (balanceRes.ok) {
        const data = await balanceRes.json();
        setBalance(data.amount);
      }
      if (scheduleRes.ok) {
        const data = await scheduleRes.json();
        setPaySchedule(data);
      }
      if (prefsRes.ok) {
        const data = await prefsRes.json();
        setNotificationPrefs(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const checkPushSupport = async () => {
    console.log('=== [Push] PERMISSION CHECK START ===');
    
    // Step 1: Check basic browser support
    const hasNotification = 'Notification' in window;
    const hasServiceWorker = 'serviceWorker' in navigator;
    const hasPushManager = 'PushManager' in window;
    
    console.log('[Push] Browser support:', { hasNotification, hasServiceWorker, hasPushManager });
    
    const supported = hasNotification && hasServiceWorker && hasPushManager;
    setPushSupported(supported);
    
    if (!supported) {
      console.log('[Push] Push notifications NOT supported on this browser');
      setPushEnabled(false);
      return;
    }
    
    // Step 2: Check if running as installed PWA (Home Screen app)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    console.log('[Push] Standalone/PWA mode:', isStandalone);
    
    // Step 3: Check actual Notification.permission
    const permission = Notification.permission;
    console.log('[Push] Notification.permission:', permission);
    
    // If permission is NOT granted, force toggle OFF
    if (permission !== 'granted') {
      console.log('[Push] Permission is NOT granted - forcing toggle OFF');
      setPushEnabled(false);
      return;
    }
    
    // Step 4: Check service worker registration
    let registration;
    try {
      registration = await navigator.serviceWorker.ready;
      console.log('[Push] Service worker ready:', {
        scope: registration.scope,
        active: registration.active ? registration.active.state : 'none'
      });
    } catch (swError) {
      console.error('[Push] Service worker NOT ready:', swError);
      setPushEnabled(false);
      return;
    }
    
    // Step 5: Check for existing push subscription
    let subscription;
    try {
      subscription = await registration.pushManager.getSubscription();
      console.log('[Push] Existing subscription:', subscription ? {
        endpoint: subscription.endpoint.substring(0, 60) + '...',
        expirationTime: subscription.expirationTime
      } : 'NONE');
    } catch (subError) {
      console.error('[Push] Error getting subscription:', subError);
      setPushEnabled(false);
      return;
    }
    
    // If no subscription exists, toggle should be OFF
    if (!subscription) {
      console.log('[Push] No active subscription - toggle should be OFF');
      setPushEnabled(false);
      return;
    }
    
    // Step 6: Verify subscription exists on backend
    try {
      const verifyRes = await fetch(`${API}/notifications/verify-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ endpoint: subscription.endpoint })
      });
      
      if (verifyRes.ok) {
        const verifyData = await verifyRes.json();
        console.log('[Push] Backend verification:', verifyData);
        
        if (!verifyData.exists) {
          console.log('[Push] Subscription NOT found on backend - toggle OFF');
          setPushEnabled(false);
          return;
        }
      } else {
        console.log('[Push] Backend verification failed - status:', verifyRes.status);
        // If verification endpoint fails, we can't confirm - be conservative
        setPushEnabled(false);
        return;
      }
    } catch (verifyError) {
      console.error('[Push] Backend verification error:', verifyError);
      setPushEnabled(false);
      return;
    }
    
    // Step 7: ALL checks passed - notifications are TRULY enabled
    console.log('[Push] ✅ ALL CHECKS PASSED - Notifications are TRULY enabled');
    console.log('=== [Push] PERMISSION CHECK END ===');
    setPushEnabled(true);
  };

  // Function to complete notification setup after permission is granted
  const completeNotificationSetup = async () => {
    console.log('[Push] Completing notification setup...');
    setNotificationStep('checking');
    
    try {
      const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      if (!vapidKey) {
        throw new Error('VAPID key not configured');
      }
      
      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey)
        });
      }
      
      // Save to backend
      const response = await fetch(`${API}/notifications/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          keys: {
            p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
            auth: arrayBufferToBase64(subscription.getKey('auth'))
          }
        })
      });
      
      if (response.ok) {
        setNotificationStep('success');
        setPushEnabled(true);
        setTimeout(() => {
          setShowNotificationGuide(false);
          toast.success('Notifications enabled!');
        }, 2000);
      } else {
        throw new Error('Backend save failed');
      }
    } catch (error) {
      console.error('[Push] Setup completion error:', error);
      setNotificationStep('blocked');
    }
  };

  const handleUpdateBalance = async () => {
    try {
      const response = await fetch(`${API}/balance`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ amount: parseFloat(newBalance) })
      });

      if (response.ok) {
        setBalance(parseFloat(newBalance));
        setShowBalanceModal(false);
        toast.success('Balance updated');
      }
    } catch (error) {
      toast.error('Failed to update balance');
    }
  };

  const handleUpdatePaySchedule = async () => {
    try {
      const response = await fetch(`${API}/forecast/pay-schedule`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          pay_frequency: newPaySchedule.pay_frequency,
          next_payday: newPaySchedule.next_payday
        })
      });

      if (response.ok) {
        setPaySchedule(newPaySchedule);
        setShowPayScheduleModal(false);
        toast.success('Pay schedule updated');
      }
    } catch (error) {
      toast.error('Failed to update pay schedule');
    }
  };

  const handleToggleNotification = async (key, value) => {
    const updated = { ...notificationPrefs, [key]: value };
    setNotificationPrefs(updated);

    try {
      await fetch(`${API}/notifications/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updated)
      });
    } catch (error) {
      // Revert on error
      setNotificationPrefs(notificationPrefs);
      toast.error('Failed to update preferences');
    }
  };

  const handleEnablePush = async () => {
    console.log('=== [Push] TOGGLE CLICKED ===');
    console.log('[Push] Current state:', { pushEnabled, pushLoading });
    
    if (pushEnabled) {
      // === UNSUBSCRIBE FLOW ===
      console.log('[Push] Starting UNSUBSCRIBE flow...');
      try {
        setPushLoading(true);
        
        const registration = await navigator.serviceWorker.ready;
        console.log('[Push] Service worker ready for unsubscribe');
        
        const subscription = await registration.pushManager.getSubscription();
        console.log('[Push] Current subscription:', subscription ? 'Found' : 'None');
        
        if (subscription) {
          const unsubResult = await subscription.unsubscribe();
          console.log('[Push] Unsubscribe result:', unsubResult);
        }
        
        // Remove from backend
        const deleteRes = await fetch(`${API}/notifications/subscribe`, {
          method: 'DELETE',
          credentials: 'include',
        });
        console.log('[Push] Backend delete response:', deleteRes.status);
        
        setPushEnabled(false);
        toast.success('Notifications disabled');
        console.log('[Push] ✅ Unsubscribe complete');
        
      } catch (error) {
        console.error('[Push] ❌ Unsubscribe error:', error);
        toast.error('Failed to disable notifications');
      } finally {
        setPushLoading(false);
      }
      return;
    }
    
    // === SUBSCRIBE FLOW WITH GUIDE ===
    console.log('[Push] Starting SUBSCRIBE flow with guide...');
    setShowNotificationGuide(true);
    setNotificationStep('checking');
    setPushLoading(true);
    
    try {
      // Step 1: Check current permission state
      const currentPermission = Notification.permission;
      console.log('[Push] Current permission:', currentPermission);
      
      // Step 2: Request permission if not already granted
      let permission = currentPermission;
      if (currentPermission !== 'granted') {
        console.log('[Push] Requesting permission...');
        
        try {
          permission = await Notification.requestPermission();
          console.log('[Push] Permission result:', permission);
        } catch (permError) {
          console.error('[Push] Permission request error:', permError);
          setNotificationStep('blocked');
          setPushLoading(false);
          return;
        }
      }
      
      // Step 3: Handle permission result
      if (permission === 'denied' || permission === 'default') {
        console.log('[Push] Permission not granted:', permission);
        setNotificationStep('blocked');
        setPushLoading(false);
        setPushEnabled(false);
        return;
      }
      
      if (permission !== 'granted') {
        console.log('[Push] Unexpected permission state:', permission);
        setNotificationStep('blocked');
        setPushLoading(false);
        setPushEnabled(false);
        return;
      }
      
      console.log('[Push] Permission GRANTED - completing setup...');
      
      // Step 4: Complete the setup
      const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      if (!vapidKey) {
        console.error('[Push] VAPID key not configured');
        setNotificationStep('blocked');
        setPushLoading(false);
        return;
      }
      
      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey)
        });
      }
      
      if (!subscription || !subscription.endpoint) {
        console.error('[Push] Failed to create subscription');
        setNotificationStep('blocked');
        setPushLoading(false);
        return;
      }
      
      // Save to backend
      const response = await fetch(`${API}/notifications/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          keys: {
            p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
            auth: arrayBufferToBase64(subscription.getKey('auth'))
          }
        })
      });
      
      if (!response.ok) {
        throw new Error('Backend save failed');
      }
      
      // Success!
      console.log('[Push] ✅ Notifications enabled successfully!');
      setNotificationStep('success');
      setPushEnabled(true);
      
      // Auto-close after success
      setTimeout(() => {
        setShowNotificationGuide(false);
      }, 2500);
      
    } catch (error) {
      console.error('[Push] ❌ Setup error:', error);
      setNotificationStep('blocked');
      setPushEnabled(false);
    } finally {
      setPushLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const handleDeleteAccount = async () => {
    try {
      await deleteAccount();
      navigate('/');
      toast.success('Account deleted');
    } catch (error) {
      toast.error('Failed to delete account');
    }
  };

  const handleCurrencyChange = async (newCurrency) => {
    setSelectedCurrency(newCurrency);
    const success = await updateCurrency(newCurrency);
    if (success) {
      toast.success(`Currency changed to ${CURRENCIES[newCurrency].name}`);
    } else {
      toast.error('Failed to update currency');
      setSelectedCurrency(user?.currency || 'GBP');
    }
  };

  const shareMessage = `I've been using Payday Control to see exactly what I can safely spend each day until payday. It's really helpful for staying on track with money.\n\n${APP_URL}`;

  const handleShare = async () => {
    // Check if Web Share API is supported
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Payday Control',
          text: `I've been using Payday Control to see exactly what I can safely spend each day until payday. It's really helpful for staying on track with money.`,
          url: APP_URL
        });
        toast.success('Thanks for sharing!');
      } catch (error) {
        if (error.name !== 'AbortError') {
          // User cancelled - show modal as fallback
          setShowShareModal(true);
        }
      }
    } else {
      // Show modal for desktop
      setShowShareModal(true);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(APP_URL);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(shareMessage);
      toast.success('Message copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy message');
    }
  };

  // Helper functions
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  return (
    <div className="min-h-screen bg-[#0F1224] page-container" data-testid="settings-page">
      {/* Header */}
      <header className="px-4 pt-4 pb-2 safe-top">
        <h1 className="font-heading text-2xl font-bold text-white">Settings</h1>
        <p className="text-[#6B7280]">Manage your account</p>
      </header>

      <main className="px-4 py-4 space-y-6">
        {/* Profile Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4"
        >
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-[#252A4A] border border-[#2D3354] flex items-center justify-center overflow-hidden">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-full h-full object-cover" />
              ) : (
                <User className="w-8 h-8 text-[#6B7280]" />
              )}
            </div>
            <div className="flex-1">
              <p className="text-white font-semibold text-lg">{user?.name}</p>
              <p className="text-[#6B7280] text-sm">{user?.email}</p>
            </div>
            {user?.role === 'admin' && (
              <span className="px-2 py-1 text-xs font-medium bg-[#63E6E2]/10 text-[#63E6E2] rounded-full">
                Admin
              </span>
            )}
          </div>
        </motion.div>

        {/* Financial Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-3"
        >
          <h2 className="text-[#6B7280] text-xs uppercase tracking-wider font-medium px-1">
            Financial Settings
          </h2>
          
          <button
            onClick={() => {
              setNewBalance(balance?.toString() || '');
              setShowBalanceModal(true);
            }}
            className="w-full bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4 flex items-center justify-between"
            data-testid="update-balance-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#63E6E2]/10 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-[#63E6E2]" />
              </div>
              <div className="text-left">
                <p className="text-white font-medium">Current Balance</p>
                <p className="text-[#6B7280] text-sm">{formatCurrency(balance || 0, selectedCurrency)}</p>
              </div>
            </div>
            <Edit2 className="w-5 h-5 text-[#6B7280]" />
          </button>

          <button
            onClick={() => {
              setNewPaySchedule({
                pay_frequency: paySchedule?.pay_frequency || 'monthly',
                next_payday: paySchedule?.next_payday?.split('T')[0] || ''
              });
              setShowPayScheduleModal(true);
            }}
            className="w-full bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4 flex items-center justify-between"
            data-testid="update-pay-schedule-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#2E93fA]/10 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-[#2E93fA]" />
              </div>
              <div className="text-left">
                <p className="text-white font-medium">Pay Schedule</p>
                <p className="text-[#6B7280] text-sm">
                  {paySchedule ? `${paySchedule.pay_frequency}` : 'Not set'}
                </p>
              </div>
            </div>
            <Edit2 className="w-5 h-5 text-[#6B7280]" />
          </button>

          {/* Currency Selection */}
          <div className="w-full bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#10B981]/10 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-[#10B981]" />
                </div>
                <div>
                  <p className="text-white font-medium">Currency</p>
                  <p className="text-[#6B7280] text-sm">Display currency for all amounts</p>
                </div>
              </div>
              <Select value={selectedCurrency} onValueChange={handleCurrencyChange}>
                <SelectTrigger className="w-28 bg-[#252A4A] border-[#2D3354] text-white" data-testid="currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1B1F3A] border-[#2D3354]">
                  {Object.entries(CURRENCIES).map(([code, curr]) => (
                    <SelectItem 
                      key={code} 
                      value={code}
                      className="text-white hover:bg-[#252A4A]"
                    >
                      {curr.symbol} {code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </motion.div>

        {/* Notification Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-3"
        >
          <h2 className="text-[#6B7280] text-xs uppercase tracking-wider font-medium px-1">
            Notifications
          </h2>
          
          <div className="bg-[#1B1F3A] rounded-xl border border-[#2D3354] divide-y divide-[#2D3354]">
            {/* Push Notifications - Always show with accurate state */}
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    pushEnabled ? 'bg-[#22C55E]/10' : 'bg-[#F59E0B]/10'
                  }`}>
                    <Bell className={`w-5 h-5 ${pushEnabled ? 'text-[#22C55E]' : 'text-[#F59E0B]'}`} />
                  </div>
                  <div>
                    <p className="text-white font-medium">Push Notifications</p>
                    <p className="text-[#6B7280] text-sm">
                      {!pushSupported 
                        ? 'Not supported on this device' 
                        : pushLoading
                          ? 'Setting up...'
                          : pushEnabled 
                            ? '✓ Enabled - 8 AM, 12 PM, 6 PM & 10 PM' 
                            : 'Tap to enable alerts'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={pushEnabled}
                  onCheckedChange={handleEnablePush}
                  disabled={pushLoading || !pushSupported}
                  data-testid="push-notifications-switch"
                />
              </div>
            </div>

            <div className="p-4 flex items-center justify-between">
              <div>
                <p className="text-white font-medium">Daily Summary</p>
                <p className="text-[#6B7280] text-sm">Morning spending overview</p>
              </div>
              <Switch
                checked={notificationPrefs.daily_summary}
                onCheckedChange={(v) => handleToggleNotification('daily_summary', v)}
                data-testid="daily-summary-switch"
              />
            </div>

            <div className="p-4 flex items-center justify-between">
              <div>
                <p className="text-white font-medium">Bill Reminders</p>
                <p className="text-[#6B7280] text-sm">Day before bills are due</p>
              </div>
              <Switch
                checked={notificationPrefs.bill_reminders}
                onCheckedChange={(v) => handleToggleNotification('bill_reminders', v)}
                data-testid="bill-reminders-switch"
              />
            </div>

            <div className="p-4 flex items-center justify-between">
              <div>
                <p className="text-white font-medium">Low Balance Alert</p>
                <p className="text-[#6B7280] text-sm">When budget gets tight</p>
              </div>
              <Switch
                checked={notificationPrefs.low_balance_alert}
                onCheckedChange={(v) => handleToggleNotification('low_balance_alert', v)}
                data-testid="low-balance-switch"
              />
            </div>

            <div className="p-4 flex items-center justify-between">
              <div>
                <p className="text-white font-medium">Payday Reminder</p>
                <p className="text-[#6B7280] text-sm">When payday is approaching</p>
              </div>
              <Switch
                checked={notificationPrefs.payday_reminder}
                onCheckedChange={(v) => handleToggleNotification('payday_reminder', v)}
                data-testid="payday-reminder-switch"
              />
            </div>
          </div>
        </motion.div>

        {/* Account Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-3"
        >
          <h2 className="text-[#6B7280] text-xs uppercase tracking-wider font-medium px-1">
            Account
          </h2>
          
          {/* Share Button */}
          <button
            onClick={handleShare}
            className="w-full bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4 flex items-center justify-between"
            data-testid="share-app-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#8B5CF6]/10 flex items-center justify-center">
                <Share2 className="w-5 h-5 text-[#8B5CF6]" />
              </div>
              <div className="text-left">
                <p className="text-white font-medium">Share Payday Control</p>
                <p className="text-[#6B7280] text-sm">Invite friends & family</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-[#6B7280]" />
          </button>

          {user?.role === 'admin' && (
            <button
              onClick={() => navigate('/admin')}
              className="w-full bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4 flex items-center justify-between"
              data-testid="admin-panel-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#63E6E2]/10 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-[#63E6E2]" />
                </div>
                <p className="text-white font-medium">Admin Dashboard</p>
              </div>
              <ChevronRight className="w-5 h-5 text-[#6B7280]" />
            </button>
          )}

          <button
            onClick={handleLogout}
            className="w-full bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4 flex items-center gap-3"
            data-testid="logout-btn"
          >
            <div className="w-10 h-10 rounded-lg bg-[#2D3354] flex items-center justify-center">
              <LogOut className="w-5 h-5 text-white" />
            </div>
            <p className="text-white font-medium">Log Out</p>
          </button>

          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="w-full bg-[#1B1F3A] rounded-xl border border-[#FF3B30]/30 p-4 flex items-center gap-3"
            data-testid="delete-account-btn"
          >
            <div className="w-10 h-10 rounded-lg bg-[#FF3B30]/10 flex items-center justify-center">
              <Trash2 className="w-5 h-5 text-[#FF3B30]" />
            </div>
            <p className="text-[#FF3B30] font-medium">Delete Account</p>
          </button>
        </motion.div>

        {/* Legal Footer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="pt-6 pb-4"
        >
          <LegalFooter />
          
          {/* Version indicator */}
          <div className="text-center mt-4 pt-4 border-t border-[#2D3354]">
            <p className="text-[#4B5563] text-xs">
              Version 1.5.4 • Build 20260312c
            </p>
          </div>
        </motion.div>
      </main>

      {/* Mobile Navigation */}
      <MobileNav />

      {/* Update Balance Modal */}
      <Dialog open={showBalanceModal} onOpenChange={setShowBalanceModal}>
        <DialogContent className="bg-[#1B1F3A] border-[#2D3354] text-white max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Update Balance</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4 pb-4">
            <div className="space-y-2">
              <Label className="text-[#9CA3AF]">Current Balance</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]">£</span>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  value={newBalance}
                  onChange={(e) => setNewBalance(e.target.value)}
                  className="pl-7 h-12 bg-[#252A4A] border-[#2D3354] text-white text-lg focus:border-[#63E6E2]"
                  data-testid="new-balance-input"
                />
              </div>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowBalanceModal(false)}
                className="flex-1 h-12 border-[#2D3354] text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateBalance}
                className="flex-1 h-12 bg-[#63E6E2] text-[#0F1224] hover:bg-[#4DD4CF]"
                data-testid="save-balance-btn"
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Update Pay Schedule Modal */}
      <Dialog open={showPayScheduleModal} onOpenChange={setShowPayScheduleModal}>
        <DialogContent className="bg-[#1B1F3A] border-[#2D3354] text-white max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Update Pay Schedule</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4 pb-4">
            <div className="space-y-2">
              <Label className="text-[#9CA3AF]">Pay Frequency</Label>
              <select
                value={newPaySchedule.pay_frequency}
                onChange={(e) => setNewPaySchedule({ ...newPaySchedule, pay_frequency: e.target.value })}
                className="w-full h-12 bg-[#252A4A] border border-[#2D3354] rounded-md text-white px-3 focus:border-[#63E6E2]"
                data-testid="pay-frequency-select"
              >
                <option value="weekly">Weekly</option>
                <option value="biweekly">Bi-weekly (Every 2 weeks)</option>
                <option value="lunar">Lunar (Every 4 weeks / 28 days)</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-[#9CA3AF]">Next Payday</Label>
              <Input
                type="date"
                value={newPaySchedule.next_payday}
                onChange={(e) => setNewPaySchedule({ ...newPaySchedule, next_payday: e.target.value })}
                className="h-12 bg-[#252A4A] border-[#2D3354] text-white focus:border-[#63E6E2]"
                data-testid="next-payday-input"
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowPayScheduleModal(false)}
                className="flex-1 h-12 border-[#2D3354] text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdatePaySchedule}
                className="flex-1 h-12 bg-[#63E6E2] text-[#0F1224] hover:bg-[#4DD4CF]"
                data-testid="save-pay-schedule-btn"
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Account Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="bg-[#1B1F3A] border-[#2D3354] text-white max-w-sm mx-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete Account?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#9CA3AF]">
              This will permanently delete your account and all your data including bills, spending history, and settings. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#2D3354] text-white hover:bg-white/5">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              className="bg-red-500 text-white hover:bg-red-600"
              data-testid="confirm-delete-account-btn"
            >
              Delete Account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Modal (for desktop/fallback) */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="bg-[#1B1F3A] border-[#2D3354] text-white max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Share2 className="w-5 h-5 text-[#8B5CF6]" />
              Share Payday Control
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#9CA3AF] text-sm">
              Share with friends and family to help them manage their money better!
            </p>
            
            {/* Copy Link */}
            <div className="space-y-2">
              <Label className="text-[#9CA3AF]">App Link</Label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={APP_URL}
                  className="h-12 bg-[#252A4A] border-[#2D3354] text-white"
                />
                <Button
                  onClick={handleCopyLink}
                  className="h-12 px-4 bg-[#63E6E2] text-[#0F1224] hover:bg-[#4DD4CF]"
                  data-testid="copy-link-btn"
                >
                  {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </Button>
              </div>
            </div>

            {/* Share Message */}
            <div className="space-y-2">
              <Label className="text-[#9CA3AF]">Share Message</Label>
              <div className="p-3 bg-[#252A4A] rounded-lg border border-[#2D3354]">
                <p className="text-white text-sm whitespace-pre-line">
                  {shareMessage}
                </p>
              </div>
              <Button
                onClick={handleCopyMessage}
                variant="outline"
                className="w-full border-[#2D3354] text-white hover:bg-white/5"
                data-testid="copy-message-btn"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Full Message
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Notification Setup Guide Modal */}
      <Dialog open={showNotificationGuide} onOpenChange={setShowNotificationGuide}>
        <DialogContent className="bg-[#1B1F3A] border-[#2D3354] max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-center text-xl font-bold">
              {notificationStep === 'success' ? 'Notifications Enabled!' : 
               notificationStep === 'blocked' ? 'Almost Done!' : 
               'Setting Up Notifications...'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-6">
            <AnimatePresence mode="wait">
              {/* Checking State */}
              {notificationStep === 'checking' && (
                <motion.div
                  key="checking"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#63E6E2]/20 flex items-center justify-center">
                    <Bell className="w-8 h-8 text-[#63E6E2] animate-pulse" />
                  </div>
                  <p className="text-[#9CA3AF] mb-4">
                    Please tap "Allow" when prompted to enable notifications.
                  </p>
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-2 h-2 bg-[#63E6E2] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-[#63E6E2] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-[#63E6E2] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </motion.div>
              )}

              {/* Blocked State - Show Instructions */}
              {notificationStep === 'blocked' && (
                <motion.div
                  key="blocked"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#F59E0B]/20 flex items-center justify-center">
                    <AlertCircle className="w-8 h-8 text-[#F59E0B]" />
                  </div>
                  
                  <p className="text-white font-medium mb-2">
                    To receive reminders, please enable notifications for Payday Control in your phone settings.
                  </p>
                  
                  {/* iPhone-specific instructions */}
                  {isIOS && (
                    <div className="mt-4 p-4 bg-[#252A4A] rounded-xl text-left">
                      <p className="text-[#63E6E2] font-medium mb-3 flex items-center gap-2">
                        <Smartphone className="w-4 h-4" />
                        iPhone Instructions:
                      </p>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-3 text-[#9CA3AF]">
                          <div className="w-6 h-6 rounded-full bg-[#63E6E2]/20 flex items-center justify-center text-[#63E6E2] text-xs font-bold">1</div>
                          <span>Open <strong className="text-white">Settings</strong> app</span>
                        </div>
                        <div className="flex items-center gap-3 text-[#9CA3AF]">
                          <div className="w-6 h-6 rounded-full bg-[#63E6E2]/20 flex items-center justify-center text-[#63E6E2] text-xs font-bold">2</div>
                          <span>Tap <strong className="text-white">Notifications</strong></span>
                        </div>
                        <div className="flex items-center gap-3 text-[#9CA3AF]">
                          <div className="w-6 h-6 rounded-full bg-[#63E6E2]/20 flex items-center justify-center text-[#63E6E2] text-xs font-bold">3</div>
                          <span>Find <strong className="text-white">Payday Control</strong></span>
                        </div>
                        <div className="flex items-center gap-3 text-[#9CA3AF]">
                          <div className="w-6 h-6 rounded-full bg-[#63E6E2]/20 flex items-center justify-center text-[#63E6E2] text-xs font-bold">4</div>
                          <span>Turn on <strong className="text-white">Allow Notifications</strong></span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Android instructions */}
                  {!isIOS && (
                    <div className="mt-4 p-4 bg-[#252A4A] rounded-xl text-left">
                      <p className="text-[#63E6E2] font-medium mb-3 flex items-center gap-2">
                        <Smartphone className="w-4 h-4" />
                        Android Instructions:
                      </p>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-3 text-[#9CA3AF]">
                          <div className="w-6 h-6 rounded-full bg-[#63E6E2]/20 flex items-center justify-center text-[#63E6E2] text-xs font-bold">1</div>
                          <span>Open <strong className="text-white">Settings</strong></span>
                        </div>
                        <div className="flex items-center gap-3 text-[#9CA3AF]">
                          <div className="w-6 h-6 rounded-full bg-[#63E6E2]/20 flex items-center justify-center text-[#63E6E2] text-xs font-bold">2</div>
                          <span>Tap <strong className="text-white">Apps & notifications</strong></span>
                        </div>
                        <div className="flex items-center gap-3 text-[#9CA3AF]">
                          <div className="w-6 h-6 rounded-full bg-[#63E6E2]/20 flex items-center justify-center text-[#63E6E2] text-xs font-bold">3</div>
                          <span>Find <strong className="text-white">Payday Control</strong></span>
                        </div>
                        <div className="flex items-center gap-3 text-[#9CA3AF]">
                          <div className="w-6 h-6 rounded-full bg-[#63E6E2]/20 flex items-center justify-center text-[#63E6E2] text-xs font-bold">4</div>
                          <span>Enable <strong className="text-white">Notifications</strong></span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="mt-6 space-y-3">
                    <Button
                      onClick={() => {
                        // Try to re-check after user returns from settings
                        checkPushSupport();
                        completeNotificationSetup();
                      }}
                      className="w-full h-12 bg-[#63E6E2] text-[#0F1224] hover:bg-[#4DD4CF] font-semibold"
                    >
                      I've Enabled Notifications
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowNotificationGuide(false)}
                      className="w-full h-12 border-[#2D3354] text-[#9CA3AF] hover:bg-[#252A4A]"
                    >
                      Maybe Later
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Success State */}
              {notificationStep === 'success' && (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="text-center"
                >
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#22C55E]/20 flex items-center justify-center">
                    <CheckCircle className="w-10 h-10 text-[#22C55E]" />
                  </div>
                  <p className="text-white font-medium text-lg mb-2">
                    Notifications Enabled!
                  </p>
                  <p className="text-[#9CA3AF] text-sm">
                    You'll receive reminders at 8 AM, 12 PM, 6 PM & 10 PM
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
